import { Badge, Card, DataTable, EmptyState, Loading, PageHead, formatDate } from "@corecrm/ui";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMe, useOpenQuestions } from "../api/queries";
import type { Question } from "../api/types";
import { LoadError } from "../components/bits";
import { QuestionDialog, QuestionItem } from "../components/Questions";

/** Open questions grouped by project, in the order the projects first appear. */
export function byProject(questions: Question[]): { project: Question["project"]; items: Question[] }[] {
  const groups = new Map<string, { project: Question["project"]; items: Question[] }>();
  for (const q of questions) {
    const group = groups.get(q.project.id) ?? { project: q.project, items: [] };
    group.items.push(q);
    groups.set(q.project.id, group);
  }
  return [...groups.values()];
}

/** A project's questions grouped by who holds them: yours first, then the others by name, and
    the unassigned ones (everyone on your team sees those) last. */
export function byAssignee(
  questions: Question[],
  meId?: string,
): { key: string; name: string; items: Question[] }[] {
  const groups = new Map<string, { key: string; name: string; items: Question[] }>();
  for (const q of questions) {
    const key = q.assignee?.id ?? "none";
    const group = groups.get(key) ?? { key, name: q.assignee?.name ?? "Unassigned", items: [] };
    group.items.push(q);
    groups.set(key, group);
  }
  const rank = (g: { key: string }) => (g.key === meId ? 0 : g.key === "none" ? 2 : 1);
  return [...groups.values()].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

type Row = { kind: "group"; id: string; name: string; count: number } | { kind: "question"; q: Question };

/** The project manager's view of a project: every open question, a group per assignee. */
function ManagerTable({
  questions,
  meId,
  onOpen,
}: {
  questions: Question[];
  meId?: string;
  onOpen: (q: Question) => void;
}) {
  const rows: Row[] = byAssignee(questions, meId).flatMap((g) => [
    { kind: "group" as const, id: g.key, name: g.name, count: g.items.length },
    ...g.items.map((q) => ({ kind: "question" as const, q })),
  ]);
  return (
    <DataTable<Row>
      label="Open questions by assignee"
      rows={rows}
      rowKey={(r) => (r.kind === "group" ? `group-${r.id}` : r.q.id)}
      sectionRow={(r) =>
        r.kind === "group"
          ? { level: 2, content: `${r.name}${r.id === meId ? " (you)" : ""} · ${r.count}` }
          : null
      }
      columns={[
        {
          header: "Question",
          cell: (r) =>
            r.kind === "question" ? (
              <button type="button" className="task-title" onClick={() => onOpen(r.q)}>
                <strong>{r.q.title}</strong>
              </button>
            ) : null,
        },
        {
          header: "Asked by",
          cell: (r) => (r.kind === "question" ? `${r.q.asked_by} · ${formatDate(r.q.created_at)}` : null),
        },
        { header: "Task", cell: (r) => (r.kind === "question" ? (r.q.task?.title ?? "") : null) },
        {
          header: "Assigned to",
          cell: (r) => (r.kind === "question" ? (r.q.assignee?.name ?? "Unassigned") : null),
        },
        {
          header: "Status",
          cell: (r) => (r.kind === "question" ? <Badge kind="acc">Waiting on you</Badge> : null),
        },
      ]}
    />
  );
}

/** What's waiting on you to answer, by project: the questions assigned to you and the
    unassigned ones; on a project where you're your team's project manager, every open question,
    in a table grouped by assignee. */
export function QuestionsPage() {
  const questions = useOpenQuestions();
  const me = useMe().data;
  const [opened, setOpened] = useState<Question | null>(null);
  if (questions.isPending) return <Loading />;
  if (questions.error)
    return (
      <LoadError
        error={questions.error}
        onRetry={() => questions.refetch()}
        what="questions"
        back={["/", "Home"]}
      />
    );
  return (
    <>
      <PageHead title="Open questions" sub="What your project team needs from you" />
      {questions.data.length === 0 ? (
        <EmptyState>Nothing to answer right now.</EmptyState>
      ) : (
        byProject(questions.data).map((g) => (
          <Card
            key={g.project.id}
            title={
              <h2>
                <Link to={`/projects/${g.project.id}`}>{g.project.name}</Link>
              </h2>
            }
          >
            {g.items.some((q) => q.as_manager) ? (
              <ManagerTable questions={g.items} meId={me?.id} onOpen={setOpened} />
            ) : (
              <div className="item-list">
                {g.items.map((q) => (
                  <QuestionItem key={q.id} question={q} onOpen={setOpened} />
                ))}
              </div>
            )}
          </Card>
        ))
      )}
      {opened && <QuestionDialog question={opened} onClose={() => setOpened(null)} />}
    </>
  );
}
